<template>
  <div :class="['checkradio-container', { 'checkradio-checked': checkValue }, { 'invalid-checked': state.notValid }]">
    <label :class="['checkradio-label', type, { 'invalid-checked-label': state.notValid }]" :for="compId">
      <input
        :id="compId"
        ref="input"
        :class="['checkradio-input', type]"
        :type="type"
        :name="name"
        :value="value"
        :checked="checkValue"
        :disabled="disabled"
        :aria-label="ariaLabel"
        @change="inputChange"
      />
      <slot>{{ capitalizedType }} Label</slot>
    </label>
    <div v-if="state.notValid && errorMessage" class="checkradio-invalid-container">
      <ErrorWarningFill />
      <p>
        <slot name="checkboxError"> {{ capitalizedType }} error message. </slot>
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ChangePayload } from '../types/solo-input';
import { generateId } from '../util/generateId';

type CheckRadioEmits = 'checkbox-selected' | 'radio-selected' | 'item-toggle';

const emit = defineEmits(['checkbox-selected', 'radio-selected', 'item-toggle']);

const input = ref<HTMLInputElement | null>(null);

const props = defineProps({
  checkOverride: {
    type: Boolean,
    default: false,
  },
  overrideValue: {
    type: Boolean,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  emitInputChange: {
    type: String as () => CheckRadioEmits,
    default: undefined,
  },
  errorMessage: {
    type: Boolean,
    default: true,
  },
  type: {
    type: String,
    default: 'checkbox',
    validator: (value: string) => {
      const allowedValues = ['checkbox', 'radio'];
      return allowedValues.includes(value);
    },
  },
  isDefault: {
    type: Boolean,
    default: false,
  },
  name: {
    type: String,
    default: undefined,
  },
  notValidOverride: {
    type: Boolean,
    default: false,
  },
  radioChecked: {
    type: String,
    default: undefined,
  },
  value: {
    type: String,
    required: true,
  },
  ariaLabel: {
    type: String,
    default: undefined,
  },
});

const state = reactive({
  checked: false,
  notValid: false,
});

const radioCheckSwap = computed<boolean>(() => props.radioChecked === props.value);
const checkValue = computed<boolean>(() => {
  let value;
  if (props.checkOverride) {
    value = props.overrideValue;
  } else {
    value = props.type === 'checkbox' ? state.checked : radioCheckSwap.value;
  }
  return value;
});
const compId = computed<string>(() => generateId('checkradio'));
const capitalizedType = computed<string>(() => props.type.charAt(0).toUpperCase() + props.type.slice(1));

watch(
  () => props.notValidOverride,
  (newValue) => {
    state.notValid = newValue;
  }
);

watch(
  () => props.overrideValue,
  (newValue) => {
    if (props.emitInputChange) {
      const payload = {
        value: props.value,
        checked: newValue,
        name: props.name,
      };
      emit(props.emitInputChange, payload);
    }
  }
);

onBeforeMount(() => {
  if (props.isDefault) state.checked = true;
  if (props.notValidOverride) state.notValid = props.notValidOverride;
});

onBeforeUpdate(() => {
  state.checked = returnActualChecked();
  if (props.notValidOverride) state.notValid = props.notValidOverride;
});

function inputChange() {
  state.checked = returnActualChecked();
  if (props.emitInputChange) {
    const payload = {
      value: props.value,
      checked: state.checked,
      name: props.name,
    } as ChangePayload;
    emit(props.emitInputChange, payload);
  }
}
function returnActualChecked(): boolean {
  return input.value?.checked || false;
}
</script>

<style lang="scss" scoped>
.checkradio-container {
  &.checkradio-checked {
    .checkradio-label {
      color: $color-neutral-black;

      &:hover,
      &:focus-within {
        color: $color-neutral-black;
      }
    }
  }

  &.invalid-checked {
    .checkradio-label {
      color: $color-danger-dark;

      &:focus-within,
      &:hover {
        color: $color-danger-dark;
      }
    }

    .checkradio-input {
      border-color: $color-danger-default;

      &:hover,
      &:focus {
        border-color: $color-danger-dark;
      }
    }

    &.checkradio-checked {
      .checkradio-input {
        background-color: $color-danger-default;
        border-color: $color-danger-default;

        &:hover,
        &:focus {
          background-color: $color-danger-dark;
          border-color: $color-danger-dark;
        }
      }
    }
  }
}

.checkradio-label {
  display: flex;
  align-items: center;
  font-weight: 500;
  font-size: 0.875rem;
  color: $color-neutral-cool-900;

  &:hover {
    color: $color-neutral-black;
    cursor: pointer;
  }

  &:focus-within {
    color: $color-secondary-500;
  }
}

.checkradio-input {
  height: 1.5rem;
  margin: 0 0.5rem 0 0;
  width: 1.5rem;

  &.checkbox,
  &.radio {
    appearance: none;
    border: 2px solid $color-neutral-cool-700;
    border-radius: 4px;
    color: $color-neutral-white;
    font-size: 100%;
    text-align: center;
    box-shadow: inset 0 0 0 1rem;
    position: relative;
    outline: none;
    transition: box-shadow 0.3s, background-color 0.3s, background-image 0.3s, border-color 0.2s;

    &::after {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }

    &:hover {
      border-color: $color-neutral-black;
      cursor: pointer;

      &:disabled {
        cursor: not-allowed;
        background-color: $color-neutral-cool-300;
        border-color: $color-neutral-cool-300;
      }
    }

    &:focus {
      border-color: $color-primary-500;
    }

    &:checked {
      border-color: $color-primary-500;
      background-color: $color-primary-500;
      box-shadow: inset 0 0 0 0;

      &:disabled {
        cursor: not-allowed;
        background-color: $color-neutral-cool-300;
        border-color: $color-neutral-cool-300;
      }

      &:focus {
        border-color: $color-primary-600;
        background-color: $color-primary-600;
      }
    }
  }

  &.checkbox {
    &::after {
      content: '';
      background: inherit;
      background-image: url('data:image/svg+xml; utf8, <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" stroke="none" fill="white"><path fill="none" d="M0 0h24v24H0z"/><path d="M10 15.172l9.192-9.193 1.415 1.414L10 18l-6.364-6.364 1.414-1.414z"/></svg>');
      background-size: cover;
    }
  }

  &.radio {
    border-radius: 50%;

    &:checked {
      box-shadow: inset 0 0 0 0.2rem;
    }
  }
}

.checkradio-invalid-container {
  align-items: center;
  display: flex;
  margin-top: 0.45rem;
  color: $color-danger-dark;
  fill: $color-danger-dark;

  :deep(svg) {
    height: 1rem;
    margin-right: 0.25rem;
    width: 1rem;
  }

  p {
    margin: 0;
  }
}
</style>
